/**
 * SizilPro — /proposal-render
 *
 * Puppeteer screenshot target. Receives all proposal data encoded as a
 * base64 JSON payload in the query string — no auth, no Supabase calls.
 *
 * URL format: /proposal-render?payload=BASE64_JSON
 *
 * The payload (ProposalRenderPayload) is built and encoded server-side in
 * netlify/functions/generate-pdf.js before Puppeteer navigates here.
 *
 * Once all pages are mounted and fonts are loaded, the component sets
 * data-ready="true" on <body> so Puppeteer knows to capture the PDF.
 */

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import { cn } from "@/lib/utils";
import type {
  ProposalDocumentLayout,
  ProposalDocumentLayoutPage,
  TemplatePageLayoutMode,
} from "@/modules/proposal-builder/types";
import { BuilderElementPreview } from "@/modules/proposal-builder/components/builder/BuilderElementPreview";
import type {
  BrandPreviewData,
  ProposalLineItemPreview,
} from "@/modules/proposal-builder/components/builder/BuilderElementPreview";
import type { BuilderElementDraft } from "@/modules/proposal-builder/components/builder/BuilderSectionElementList";

// ---------------------------------------------------------------------------
// Route definition
// ---------------------------------------------------------------------------

const searchSchema = z.object({
  payload: z.string().optional(),
});

export const Route = createFileRoute("/proposal-render")({
  validateSearch: searchSchema,
  component: ProposalRenderPage,
});

// ---------------------------------------------------------------------------
// Payload shape — must match what generate-pdf.js encodes
// ---------------------------------------------------------------------------

export interface ProposalRenderPayload {
  layout: ProposalDocumentLayout;
  brand: {
    companyName?: string | null;
    primaryLogoUrl?: string | null;
    secondaryLogoUrl?: string | null;
    phone?: string | null;
    website?: string | null;
    email?: string | null;
    address?: string | null;
    tagline?: string | null;
    primaryColor?: string | null;
    textColor?: string | null;
    backgroundColor?: string | null;
    /** Resolved heading font CSS family (e.g. '"Milestone", Inter, sans-serif') */
    headingFontFamily?: string | null;
    headingFontUrl?: string | null;
    /** Resolved body font CSS family */
    bodyFontFamily?: string | null;
    bodyFontUrl?: string | null;
    accentColor?: string | null;
    footerText?: string | null;
    coverLetterHtml?: string | null;
    signoff?: string | null;
  };
  proposal: {
    id: string;
    prospectName?: string | null;
    prospectEmail?: string | null;
    prospectAddress?: string | null;
    subtotal?: number | null;
    taxRate?: number | null;
    taxAmount?: number | null;
    total?: number | null;
  };
  lineItems: ProposalLineItemPreview[];
  staticPages?: Array<{ title: string; contentHtml: string }>;
  includeSigningTags?: boolean;
  prebuiltMode?: boolean;
}

// ---------------------------------------------------------------------------
// Helpers — same as BuilderPagePreview / CanvasPageStage
// ---------------------------------------------------------------------------

interface PageSection {
  id:         string;
  layoutMode: TemplatePageLayoutMode;
  elements:   BuilderElementDraft[];
  settings?:  Record<string, string | boolean | number | null | undefined>;
}

function normalizeSections(page: ProposalDocumentLayoutPage): PageSection[] {
  if (!Array.isArray(page.sections)) return [];
  return page.sections.map((section, index) => {
    const rec =
      section && typeof section === "object" && !Array.isArray(section)
        ? (section as Record<string, unknown>)
        : {};
    const rawLayout = rec.layoutMode;
    const layoutMode: TemplatePageLayoutMode =
      rawLayout === "stack"        || rawLayout === "two_column"  ||
      rawLayout === "three_column" || rawLayout === "split_left"  ||
      rawLayout === "split_right"  || rawLayout === "grid"        ||
      rawLayout === "custom"
        ? rawLayout : "stack";
    return {
      id:       typeof rec.id === "string" && rec.id.length > 0 ? rec.id : `s_${index}`,
      layoutMode,
      elements: Array.isArray(rec.elements) ? (rec.elements as BuilderElementDraft[]) : [],
      settings: rec.settings && typeof rec.settings === "object" && !Array.isArray(rec.settings)
        ? (rec.settings as Record<string, string | boolean | number | null | undefined>)
        : undefined,
    };
  });
}

function getGridClass(layoutMode: TemplatePageLayoutMode) {
  switch (layoutMode) {
    case "two_column":   return "grid-cols-2";
    case "three_column": return "grid-cols-3";
    case "split_left":   return "grid-cols-[1.35fr_0.65fr]";
    case "split_right":  return "grid-cols-[0.65fr_1.35fr]";
    case "grid":         return "grid-cols-2";
    default:             return "grid-cols-1";
  }
}

function normalizePages(layout: ProposalDocumentLayout): ProposalDocumentLayoutPage[] {
  const pages = Array.isArray(layout?.pages) ? layout.pages : [];
  return pages
    .map((page, index) => ({
      ...page,
      id:       page.id ?? `page_${index}`,
      type:     page.type ?? "custom",
      order:    typeof page.order === "number" ? page.order : index,
      sections: Array.isArray(page.sections) ? page.sections : [],
    }))
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

// ---------------------------------------------------------------------------
// DocuSeal signing page — appended after builder pages when includeSigningTags=true
// ---------------------------------------------------------------------------

function SigningPage({
  prospectName,
  companyName,
  primaryColor,
  accentColor,
  isLast,
}: {
  prospectName: string;
  companyName: string;
  primaryColor: string;
  accentColor: string;
  isLast: boolean;
}) {
  const signerRole = "Customer";
  return (
    <div
      style={{
        width: "8.5in",
        minHeight: "11in",
        backgroundColor: "#ffffff",
        padding: "0.75in 0.75in",
        boxSizing: "border-box",
        pageBreakAfter: isLast ? "auto" : "always",
        position: "relative",
        fontFamily: "Inter, Arial, sans-serif",
        color: "#111827",
      }}
    >
      {/* Section title */}
      <div style={{ fontSize: "20px", fontWeight: 700, color: primaryColor, marginBottom: "12px", paddingBottom: "8px", borderBottom: `2px solid ${accentColor}` }}>
        Acceptance & Signature
      </div>

      <p style={{ color: "#374151", marginBottom: "8px" }}>
        By signing below, {prospectName} acknowledges review and acceptance of this proposal, including the scope of work and pricing described in this document.
      </p>
      <p style={{ color: "#374151", marginBottom: "24px" }}>
        This page is included for secure electronic signature processing.
      </p>

      {/* Authorization card */}
      <div style={{ border: "1px solid #e5e7eb", borderRadius: "16px", padding: "24px", background: "#fafafa" }}>
        <div style={{ textTransform: "uppercase", letterSpacing: "0.16em", fontSize: "10px", fontWeight: 700, color: accentColor, marginBottom: "10px" }}>
          Authorization
        </div>
        <div style={{ fontSize: "22px", fontWeight: 800, color: primaryColor, marginBottom: "6px" }}>
          {companyName} Proposal Acceptance
        </div>
        <div style={{ color: "#6b7280", marginBottom: "24px" }}>
          Signer: {prospectName}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "20px" }}>
          <div>
            <span style={{ display: "block", marginBottom: "8px", fontSize: "11px", fontWeight: 700, textTransform: "uppercase" as const, letterSpacing: "0.04em", color: "#6b7280" }}>
              Full Name
            </span>
            {/* Tags rendered in Courier New so Puppeteer embeds searchable text that DocuSeal can extract */}
            <span style={{ display: "block", minHeight: "44px", padding: "8px 0", fontSize: "16px", fontFamily: "'Courier New', Courier, monospace", fontVariantLigatures: "none" }}>
              {"{{Full Name;role=" + signerRole + ";type=text}}"}
            </span>
          </div>
          <div>
            <span style={{ display: "block", marginBottom: "8px", fontSize: "11px", fontWeight: 700, textTransform: "uppercase" as const, letterSpacing: "0.04em", color: "#6b7280" }}>
              Signature
            </span>
            <span style={{ display: "block", minHeight: "44px", padding: "8px 0", fontSize: "16px", fontFamily: "'Courier New', Courier, monospace", fontVariantLigatures: "none" }}>
              {"{{Customer Signature;role=" + signerRole + ";type=signature}}"}
            </span>
          </div>
          <div>
            <span style={{ display: "block", marginBottom: "8px", fontSize: "11px", fontWeight: 700, textTransform: "uppercase" as const, letterSpacing: "0.04em", color: "#6b7280" }}>
              Date
            </span>
            <span style={{ display: "block", minHeight: "44px", padding: "8px 0", fontSize: "16px", fontFamily: "'Courier New', Courier, monospace", fontVariantLigatures: "none" }}>
              {"{{Signed Date;role=" + signerRole + ";type=date}}"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Single rendered page — white Letter sheet
// ---------------------------------------------------------------------------

function hexToRgba(hex: string, opacity: number): string {
  const h = hex.replace("#", "");
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return hex;
  return `rgba(${r},${g},${b},${Math.max(0, Math.min(1, opacity))})`;
}

function RenderPage({
  page,
  brand,
  isLast,
}: {
  page: ProposalDocumentLayoutPage;
  brand: BrandPreviewData;
  isLast: boolean;
}) {
  const sections = normalizeSections(page);
  const hasSections = sections.some((s) => s.elements.length > 0);

  return (
    <div
      style={{
        width: "8.5in",
        minHeight: "11in",
        backgroundColor: "#ffffff",
        padding: "0.75in 0.75in",
        boxSizing: "border-box",
        pageBreakAfter: isLast ? "auto" : "always",
        position: "relative",
      }}
    >
      {hasSections ? (
        <div style={{ display: "flex", flexDirection: "column", gap: "2.5rem" }}>
          {sections.map((section, index) => {
            if (section.elements.length === 0) return null;
            const rawBg     = section.settings?.backgroundColor as string | undefined;
            const sectionBg = typeof rawBg === "string" && rawBg ? rawBg : null;
            const bgOpacity = typeof section.settings?.backgroundOpacity === "number"
              ? section.settings.backgroundOpacity / 100 : 1;
            const sectionStyle = sectionBg
              ? { backgroundColor: hexToRgba(sectionBg, bgOpacity), borderRadius: "12px", padding: "1.5rem" }
              : undefined;
            return (
              <div key={section.id} style={sectionStyle}>
                {index > 0 && !sectionBg && (
                  <div style={{ marginBottom: "2.5rem", borderTop: "1px solid rgba(0,0,0,0.05)" }} />
                )}
                <div className={cn("grid gap-8", getGridClass(section.layoutMode))}>
                  {section.elements.map((element) => (
                    <BuilderElementPreview
                      key={element.id}
                      element={element}
                      brand={brand}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", minHeight: "320px" }}>
          <p style={{ color: "#9ca3af", fontSize: "14px" }}>No content on this page.</p>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Prebuilt PDF renderer — used when there is no layout_json
// Renders: cover page · cover letter · services & pricing · signature
// ---------------------------------------------------------------------------

function fmt(n: number) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
}

function PrebuiltPage({
  children,
  isLast = false,
}: {
  children: React.ReactNode;
  isLast?: boolean;
}) {
  return (
    <div style={{
      width: "8.5in",
      minHeight: "11in",
      backgroundColor: "#ffffff",
      boxSizing: "border-box",
      pageBreakAfter: isLast ? "auto" : "always",
      position: "relative",
      display: "flex",
      flexDirection: "column",
    }}>
      {children}
    </div>
  );
}

function PrebuiltRenderPage({
  payload,
}: {
  payload: ProposalRenderPayload;
}) {
  const primary    = payload.brand.primaryColor  ?? "#1a1a2e";
  const accent     = payload.brand.accentColor   ?? payload.brand.primaryColor ?? "#FF6B00";
  const heading    = payload.brand.headingFontFamily ?? "Inter, system-ui, sans-serif";
  const body       = payload.brand.bodyFontFamily    ?? "Inter, system-ui, sans-serif";
  const footer     = payload.brand.footerText ?? "";
  const signoff    = payload.brand.signoff    ?? "Sincerely";
  const company    = payload.brand.companyName   ?? "Your Company";
  const logoUrl    = payload.brand.primaryLogoUrl ?? null;
  const coverHtml  = payload.brand.coverLetterHtml ?? null;
  const prospect   = payload.proposal.prospectName   ?? "Client";
  const email      = payload.proposal.prospectEmail  ?? null;
  const address    = payload.proposal.prospectAddress ?? null;
  const subtotal   = payload.proposal.subtotal  ?? 0;
  const taxRate    = payload.proposal.taxRate   ?? 0;
  const taxAmount  = payload.proposal.taxAmount ?? 0;
  const total      = payload.proposal.total     ?? 0;
  const lineItems   = payload.lineItems ?? [];
  const staticPages = payload.staticPages ?? [];
  const incSigning  = payload.includeSigningTags === true;

  const contactItems = [
    payload.brand.phone   ? payload.brand.phone   : null,
    payload.brand.email   ? payload.brand.email   : null,
    payload.brand.website ? payload.brand.website : null,
  ].filter((v): v is string => v !== null);

  const now = new Date();
  const dateStr = now.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

  const pageCount = 2 + (coverHtml ? 1 : 0) + 1;
  const hasSignPage = true;

  // ── Page 1: Cover ─────────────────────────────────────────────────────────
  const coverPage = (
    <PrebuiltPage>
      {/* Gradient header — takes approx 45% of page height */}
      <div style={{
        background: `linear-gradient(145deg, ${primary} 0%, ${accent} 100%)`,
        padding: "68px 72px 56px",
        color: "#ffffff",
        flexShrink: 0,
      }}>
        {/* Logo */}
        {logoUrl ? (
          <img
            src={logoUrl}
            alt={company}
            style={{ maxHeight: "80px", maxWidth: "260px", objectFit: "contain", display: "block", marginBottom: "28px" }}
          />
        ) : (
          <div style={{
            width: "64px", height: "64px", borderRadius: "50%",
            background: "rgba(255,255,255,0.25)", display: "flex",
            alignItems: "center", justifyContent: "center",
            fontSize: "26px", fontWeight: 800, color: "#fff", marginBottom: "28px",
          }}>
            {company.charAt(0)}
          </div>
        )}

        {/* Company name */}
        <div style={{ fontFamily: heading, fontSize: "42px", fontWeight: 800, lineHeight: 1.05, letterSpacing: "-0.5px" }}>
          {company}
        </div>
        {payload.brand.tagline && (
          <div style={{ marginTop: "8px", fontSize: "16px", opacity: 0.82 }}>
            {payload.brand.tagline}
          </div>
        )}

        {/* Sub-heading */}
        <div style={{ marginTop: "14px", fontSize: "16px", opacity: 0.78 }}>
          Proposal&nbsp;&middot;&nbsp;Prepared for {prospect}
        </div>

        {/* Contact bar */}
        {contactItems.length > 0 && (
          <div style={{ marginTop: "20px", display: "flex", gap: "28px", flexWrap: "wrap", fontSize: "13px", opacity: 0.70 }}>
            {contactItems.map((c, i) => <span key={i}>{c}</span>)}
          </div>
        )}
      </div>

      {/* Body — prospect card + prepared date */}
      <div style={{ padding: "52px 72px 48px", flex: 1, display: "flex", flexDirection: "column" }}>
        <div style={{
          borderLeft: `4px solid ${accent}`,
          borderRadius: "0 14px 14px 0",
          padding: "28px 32px",
          background: "#f9fafb",
          fontFamily: body,
        }}>
          <div style={{ fontFamily: heading, fontSize: "30px", fontWeight: 800, color: primary, marginBottom: "12px", lineHeight: 1.1 }}>
            {prospect}
          </div>
          {email && (
            <div style={{ color: "#4b5563", fontSize: "15px", marginBottom: "6px" }}>{email}</div>
          )}
          {address && (
            <div style={{ color: "#4b5563", fontSize: "15px", marginBottom: "6px" }}>{address}</div>
          )}
          <div style={{ color: "#9ca3af", fontSize: "13px", marginTop: "10px" }}>Prepared {dateStr}</div>
        </div>

        <div style={{ flex: 1 }} />

        {footer && (
          <div style={{ color: "#9ca3af", fontSize: "11px" }}>{footer}</div>
        )}
      </div>
    </PrebuiltPage>
  );

  // ── Page 2: Cover Letter (conditional) ────────────────────────────────────
  const coverLetterPage = coverHtml ? (
    <PrebuiltPage>
      <div style={{ padding: "0.875in 0.875in", flex: 1, display: "flex", flexDirection: "column", fontFamily: body }}>
        <div style={{ fontSize: "10px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.14em", color: accent, marginBottom: "8px" }}>
          Cover Letter
        </div>
        <div style={{
          fontFamily: heading, fontSize: "26px", fontWeight: 700, color: primary,
          paddingBottom: "16px", borderBottom: `2px solid ${accent}`, marginBottom: "32px",
        }}>
          Introduction
        </div>

        <div
          style={{ color: "#374151", fontSize: "14px", lineHeight: 1.85, flex: 1 }}
          dangerouslySetInnerHTML={{ __html: coverHtml }}
        />

        <div style={{ marginTop: "40px", paddingTop: "24px", borderTop: "1px solid #e5e7eb" }}>
          <div style={{ fontSize: "15px", color: "#374151" }}>{signoff},</div>
          <div style={{ fontSize: "17px", fontWeight: 700, color: primary, marginTop: "6px" }}>{company}</div>
        </div>

        {footer && (
          <div style={{ marginTop: "28px", color: "#9ca3af", fontSize: "11px" }}>{footer}</div>
        )}
      </div>
    </PrebuiltPage>
  ) : null;

  // ── Page 3: Services & Pricing ────────────────────────────────────────────
  const servicesPage = (
    <PrebuiltPage>
      <div style={{ padding: "0.875in 0.875in", flex: 1, display: "flex", flexDirection: "column", fontFamily: body }}>
        <div style={{ fontSize: "10px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.14em", color: accent, marginBottom: "8px" }}>
          Services &amp; Pricing
        </div>
        <div style={{
          fontFamily: heading, fontSize: "26px", fontWeight: 700, color: primary,
          paddingBottom: "16px", borderBottom: `2px solid ${accent}`, marginBottom: "28px",
        }}>
          Scope of Work
        </div>

        {/* Table */}
        <table style={{ width: "100%", borderCollapse: "collapse", borderRadius: "12px", overflow: "hidden" }}>
          <thead>
            <tr style={{ background: primary, color: "#ffffff" }}>
              <th style={{ padding: "13px 18px", textAlign: "left", fontSize: "13px", fontWeight: 600, letterSpacing: "0.04em" }}>Service</th>
              <th style={{ padding: "13px 18px", textAlign: "left", fontSize: "13px", fontWeight: 600, letterSpacing: "0.04em" }}>Frequency</th>
              <th style={{ padding: "13px 18px", textAlign: "right", fontSize: "13px", fontWeight: 600, letterSpacing: "0.04em" }}>Price</th>
            </tr>
          </thead>
          <tbody>
            {lineItems.length === 0 ? (
              <tr>
                <td colSpan={3} style={{ padding: "20px 18px", color: "#9ca3af", textAlign: "center", fontSize: "14px" }}>
                  No services included.
                </td>
              </tr>
            ) : lineItems.map((item, i) => (
              <tr key={item.id ?? i}>
                <td style={{ padding: "14px 18px", borderBottom: "1px solid #f3f4f6", verticalAlign: "top" }}>
                  <div style={{ fontWeight: 600, fontSize: "14px", color: "#111827" }}>{item.name}</div>
                  {item.description && (
                    <div style={{ fontSize: "13px", color: "#6b7280", marginTop: "4px", lineHeight: 1.6 }}>{item.description}</div>
                  )}
                </td>
                <td style={{ padding: "14px 18px", borderBottom: "1px solid #f3f4f6", color: "#6b7280", fontSize: "13px", verticalAlign: "top" }}>
                  {item.price_unit === "per_sqft" && item.square_feet != null ? (
                    <span>
                      {item.square_feet.toLocaleString()} sq ft
                      <br />
                      <span style={{ fontSize: "12px", color: "#9ca3af" }}>
                        {item.price != null ? fmt(item.price) : "—"}/sqft
                      </span>
                    </span>
                  ) : (
                    item.price_unit ?? ""
                  )}
                </td>
                <td style={{ padding: "14px 18px", borderBottom: "1px solid #f3f4f6", textAlign: "right", fontWeight: 600, fontSize: "14px", color: "#111827", whiteSpace: "nowrap", verticalAlign: "top" }}>
                  {item.price_unit === "per_sqft" && item.square_feet != null && item.price != null
                    ? fmt(item.price * item.square_feet)
                    : item.price != null ? fmt(item.price) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Totals */}
        <div style={{ marginTop: "24px", marginLeft: "auto", width: "280px" }}>
          {taxRate > 0 && (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #e5e7eb", fontSize: "13px", color: "#374151" }}>
                <span>Subtotal</span><span>{fmt(subtotal)}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid #e5e7eb", fontSize: "13px", color: "#374151" }}>
                <span>Tax ({(taxRate * 100).toFixed(1)}%)</span><span>{fmt(taxAmount)}</span>
              </div>
            </>
          )}
          <div style={{
            display: "flex", justifyContent: "space-between", padding: "13px 18px",
            background: primary, color: "#fff", fontWeight: 700, fontSize: "15px",
            borderRadius: "10px", marginTop: "8px",
          }}>
            <span>Total</span><span>{fmt(total)}</span>
          </div>
        </div>

        <div style={{ flex: 1 }} />
        {footer && <div style={{ color: "#9ca3af", fontSize: "11px", marginTop: "24px" }}>{footer}</div>}
      </div>
    </PrebuiltPage>
  );

  // ── Page 4: Signature ─────────────────────────────────────────────────────
  const signerRole = "Customer";
  const fullNameTag  = incSigning ? `{{Full Name;role=${signerRole};type=text}}` : "";
  const sigTag       = incSigning ? `{{Customer Signature;role=${signerRole};type=signature}}` : "";
  const dateTag      = incSigning ? `{{Signed Date;role=${signerRole};type=date}}` : "";

  const signaturePage = (
    <PrebuiltPage isLast>
      <div style={{ padding: "0.875in 0.875in", flex: 1, display: "flex", flexDirection: "column", fontFamily: body }}>
        <div style={{ fontSize: "10px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.14em", color: accent, marginBottom: "8px" }}>
          Acceptance &amp; Signature
        </div>
        <div style={{
          fontFamily: heading, fontSize: "26px", fontWeight: 700, color: primary,
          paddingBottom: "16px", borderBottom: `2px solid ${accent}`, marginBottom: "28px",
        }}>
          {company} Proposal Acceptance
        </div>

        <p style={{ fontSize: "14px", color: "#374151", marginBottom: "8px", lineHeight: 1.75 }}>
          By signing below, <strong>{prospect}</strong> acknowledges review and acceptance of this proposal, including the scope of work and pricing described in this document.
        </p>
        <p style={{ fontSize: "14px", color: "#374151", marginBottom: "32px", lineHeight: 1.75 }}>
          This page serves as the official acceptance of the terms outlined above.
        </p>

        <div style={{ border: "1px solid #e5e7eb", borderRadius: "16px", padding: "32px", background: "#fafafa", maxWidth: "560px" }}>
          {/* Full Name */}
          <div style={{ marginBottom: "28px" }}>
            <div style={{ fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#6b7280", marginBottom: "10px" }}>Full Name</div>
            {incSigning ? (
              <span style={{ display: "block", minHeight: "44px", padding: "8px 0", fontSize: "16px", fontFamily: "'Courier New', Courier, monospace" }}>{fullNameTag}</span>
            ) : (
              <div style={{ height: "1px", background: "#d1d5db", marginTop: "40px" }} />
            )}
          </div>

          {/* Signature */}
          <div style={{ marginBottom: "28px" }}>
            <div style={{ fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#6b7280", marginBottom: "10px" }}>Signature</div>
            {incSigning ? (
              <span style={{ display: "block", minHeight: "64px", padding: "8px 0", fontSize: "16px", fontFamily: "'Courier New', Courier, monospace" }}>{sigTag}</span>
            ) : (
              <div style={{ height: "64px", border: "1px solid #d1d5db", borderRadius: "8px", background: "#fff" }} />
            )}
          </div>

          {/* Date */}
          <div>
            <div style={{ fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#6b7280", marginBottom: "10px" }}>Date</div>
            {incSigning ? (
              <span style={{ display: "block", minHeight: "44px", padding: "8px 0", fontSize: "16px", fontFamily: "'Courier New', Courier, monospace" }}>{dateTag}</span>
            ) : (
              <div style={{ height: "1px", background: "#d1d5db", marginTop: "40px" }} />
            )}
          </div>
        </div>

        <div style={{ flex: 1 }} />
        <div style={{ color: "#9ca3af", fontSize: "11px", marginTop: "24px" }}>
          {footer || `Prepared by ${company}`}
        </div>
      </div>
    </PrebuiltPage>
  );

  // ── Static Pages ──────────────────────────────────────────────────────────
  const staticPageNodes = staticPages.map((page, i) => (
    <PrebuiltPage key={i}>
      <div style={{ padding: "0.875in 0.875in", flex: 1, display: "flex", flexDirection: "column", fontFamily: body }}>
        <div style={{ fontSize: "10px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.14em", color: accent, marginBottom: "8px" }}>
          Additional Information
        </div>
        <div style={{
          fontFamily: heading, fontSize: "26px", fontWeight: 700, color: primary,
          paddingBottom: "16px", borderBottom: `2px solid ${accent}`, marginBottom: "28px",
        }}>
          {page.title}
        </div>
        <div
          style={{ color: "#374151", fontSize: "13px", lineHeight: 1.75, flex: 1 }}
          dangerouslySetInnerHTML={{ __html: page.contentHtml }}
        />
        {footer && <div style={{ color: "#9ca3af", fontSize: "11px", marginTop: "24px" }}>{footer}</div>}
      </div>
    </PrebuiltPage>
  ));

  void pageCount;
  void hasSignPage;

  return (
    <>
      {coverPage}
      {coverLetterPage}
      {servicesPage}
      {staticPageNodes}
      {signaturePage}
    </>
  );
}

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------

function ProposalRenderPage() {
  const { payload: encodedPayload } = Route.useSearch();
  const [ready, setReady] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const payload = useMemo<ProposalRenderPayload | null>(() => {
    // Puppeteer injects the payload as window.__PROPOSAL_PAYLOAD__ via
    // evaluateOnNewDocument to avoid URL query-string length limits.
    // Fall back to the URL param for local dev / manual testing.
    const injected = (
      window as typeof window & { __PROPOSAL_PAYLOAD__?: string }
    ).__PROPOSAL_PAYLOAD__;
    const source = injected ?? encodedPayload;
    if (!source) return null;
    try {
      const json = atob(source);
      return JSON.parse(json) as ProposalRenderPayload;
    } catch {
      return null;
    }
  }, [encodedPayload]);

  const pages = useMemo(
    () => (payload ? normalizePages(payload.layout) : []),
    [payload],
  );

  const includeSigningTags = payload?.includeSigningTags === true;

  const brand: BrandPreviewData = useMemo(() => {
    if (!payload) return {};
    return {
      companyName:        payload.brand.companyName        ?? null,
      primaryLogoUrl:     payload.brand.primaryLogoUrl     ?? null,
      secondaryLogoUrl:   payload.brand.secondaryLogoUrl   ?? null,
      phone:              payload.brand.phone               ?? null,
      website:            payload.brand.website             ?? null,
      email:              payload.brand.email               ?? null,
      address:            payload.brand.address             ?? null,
      tagline:            payload.brand.tagline             ?? null,
      primaryColor:       payload.brand.primaryColor        ?? null,
      textColor:          payload.brand.textColor           ?? null,
      backgroundColor:    payload.brand.backgroundColor    ?? null,
      headingFontFamily:  payload.brand.headingFontFamily   ?? null,
      bodyFontFamily:     payload.brand.bodyFontFamily      ?? null,
      coverLetterHtml:    payload.brand.coverLetterHtml     ?? null,
      signoff:            payload.brand.signoff             ?? null,
      prospectName:       payload.proposal.prospectName     ?? null,
      prospectAddress:    payload.proposal.prospectAddress  ?? null,
      lineItems:          payload.lineItems,
      subtotal:           payload.proposal.subtotal         ?? null,
      total:              payload.proposal.total            ?? null,
    };
  }, [payload]);

  // Signal Puppeteer once all content is mounted and fonts are loaded
  useLayoutEffect(() => {
    if (!payload) return;
    const isPrebuilt = payload.prebuiltMode === true;
    if (!isPrebuilt && pages.length === 0) return;
    const timer = setTimeout(() => {
      document.fonts.ready.then(() => {
        setReady(true);
      });
    }, 200);
    return () => clearTimeout(timer);
  }, [payload, pages.length]);

  useEffect(() => {
    if (ready) {
      document.body.setAttribute("data-ready", "true");
    }
  }, [ready]);

  if (!payload) {
    return (
      <div style={{ padding: "2rem", fontFamily: "sans-serif", color: "#374151" }}>
        <h1>Proposal Render Error</h1>
        <p>No payload provided or payload could not be decoded.</p>
        <p style={{ fontSize: "12px", color: "#9ca3af" }}>
          This page is only accessible via the PDF generation service.
        </p>
      </div>
    );
  }

  const headingFontFamily = brand.headingFontFamily ?? "Inter, system-ui, sans-serif";
  const bodyFontFamily    = brand.bodyFontFamily    ?? "Inter, system-ui, sans-serif";
  const headingFontUrl    = payload.brand.headingFontUrl ?? null;
  const bodyFontUrl       = payload.brand.bodyFontUrl    ?? null;

  // Build @font-face blocks for any uploaded (non-built-in) fonts
  const fontFaceBlocks: string[] = [];
  const seenFontFaces = new Set<string>();
  const addFontFace = (family: string, url: string | null) => {
    if (!url) return;
    const name = family.split(",")[0].replace(/['"]/g, "").trim();
    if (seenFontFaces.has(name)) return;
    seenFontFaces.add(name);
    fontFaceBlocks.push(`@font-face { font-family: '${name}'; src: url('${url}'); }`);
  };
  addFontFace(headingFontFamily, headingFontUrl);
  addFontFace(bodyFontFamily, bodyFontUrl);

  return (
    <div
      ref={containerRef}
      style={{
        backgroundColor: brand.backgroundColor ?? "#ffffff",
        fontFamily: bodyFontFamily,
        color: brand.textColor ?? "#111827",
      }}
    >
      <style>{`
        @page { size: Letter; margin: 0; }
        * { box-sizing: border-box; }
        html, body { margin: 0; padding: 0; background: ${brand.backgroundColor ?? "#fff"}; }
        ${fontFaceBlocks.join("\n")}
      `}</style>

      {payload.prebuiltMode === true ? (
        <PrebuiltRenderPage payload={payload} />
      ) : (
        <>
          {pages.map((page, index) => (
            <RenderPage
              key={page.id}
              page={page}
              brand={brand}
              isLast={index === pages.length - 1 && !includeSigningTags}
            />
          ))}

          {includeSigningTags && (
            <SigningPage
              prospectName={brand.prospectName || "Customer"}
              companyName={brand.companyName || "Your Company"}
              primaryColor={brand.primaryColor || "#111827"}
              accentColor={payload.brand.accentColor || brand.primaryColor || "#374151"}
              isLast
            />
          )}
        </>
      )}
    </div>
  );
}
